import { DollarAmount } from '@strata/react-dollar-amount';
import { FormSection } from '@strata/react-form-section';
import { type ColumnDef, Grid, SecondaryData } from '@strata/react-grid';
import { DetailLabel } from '@strata/react-label-value-pairs';
import { ClaimStatusIndicator } from '@strata/react-status-indicators';
import classNames from 'classnames';

import { ClaimCodesContainer } from 'components/claims/ClaimCodesContainer';
import { type ClaimDetails } from 'store/api/medbillingApi';

const CLAIM_TOTALS_ROW_KEY = 'CLAIM_TOTALS';

interface ServicesOverviewRow {
    procedureCode: string;
    procedureCodeDisplayText: string;
    submittedAmount?: number;
    contractualObligation: number;
    payerAmount?: number;
    otherAdj: number;
    memberLiability?: number;
    adjTotal: number;
    total?: number;
    adjCodes: typeof CLAIM_TOTALS_ROW_KEY | string[];
    serviceStatus?:
        | typeof CLAIM_TOTALS_ROW_KEY
        | (typeof ClaimStatusIndicator.Statuses)[keyof typeof ClaimStatusIndicator.Statuses];
}

const formatGridData = (claimDetails: ClaimDetails): ServicesOverviewRow[] => [
    ...claimDetails.services.map((service) => ({
        procedureCode: service.procedureCode,
        procedureCodeDisplayText: service.procedureCodeDisplayText,
        submittedAmount: service.submittedAmount,
        contractualObligation: service.contractualObligation,
        payerAmount: service.payerAmount,
        otherAdj: service.otherAdj,
        memberLiability: service.memberLiability,
        adjTotal: service.adjTotal,
        total: service.total,
        adjCodes: service.adjCodes,
        serviceStatus: service.serviceStatus,
    })),
    {
        procedureCode: CLAIM_TOTALS_ROW_KEY,
        procedureCodeDisplayText: 'Claim Total',
        submittedAmount: claimDetails.submittedAmount,
        contractualObligation: claimDetails.contractualObligation,
        payerAmount: claimDetails.payerAmount,
        otherAdj: claimDetails.otherAdj,
        memberLiability: claimDetails.memberLiability,
        adjTotal: claimDetails.adjTotal,
        total: claimDetails.claimTotal,
        adjCodes: CLAIM_TOTALS_ROW_KEY,
        serviceStatus: CLAIM_TOTALS_ROW_KEY,
    },
];

const claimServiceDollarAmount = (header: string) => ({
    size: 100,
    header,
    headerStyle: { justifyContent: 'flex-end' },
    cell: ({
        getValue,
        row,
    }: {
        getValue: () => number | undefined;
        row: { original: ServicesOverviewRow };
    }) => (
        <div
            className={classNames('text-align--right', {
                'sds_claim-totals__grid-row--value-total':
                    row.original.procedureCode === CLAIM_TOTALS_ROW_KEY,
            })}
            style={{ flexGrow: 1 }}
        >
            <DollarAmount amount={getValue()} showZeroValue />
        </div>
    ),
});

const columns: ColumnDef<ServicesOverviewRow>[] = [
    {
        accessorKey: 'procedureCodeDisplayText',
        header: 'Service',
        size: 200,
    },
    {
        ...claimServiceDollarAmount('Submitted Amt.'),
        accessorKey: 'submittedAmount',
    },
    {
        ...claimServiceDollarAmount('Contractual Obligation'),
        accessorKey: 'contractualObligation',
        size: 125,
    },
    {
        ...claimServiceDollarAmount('Payer Amt.'),
        accessorKey: 'payerAmount',
    },
    {
        ...claimServiceDollarAmount('Other Adj.'),
        accessorKey: 'otherAdj',
    },
    {
        ...claimServiceDollarAmount('Member Liability'),
        accessorKey: 'memberLiability',
    },
    {
        ...claimServiceDollarAmount('Adj. Total'),
        accessorKey: 'adjTotal',
    },
    {
        ...claimServiceDollarAmount('Paid Amt.'),
        accessorKey: 'total',
    },
    {
        accessorKey: 'adjCodes',
        header: 'Adj. Codes',
        size: 125,
        cell: ({ getValue }) => {
            const value = getValue();
            return value !== CLAIM_TOTALS_ROW_KEY ? (
                <SecondaryData data={(value ?? []).join(', ')} />
            ) : null;
        },
    },
    {
        accessorKey: 'serviceStatus',
        header: 'Status',
        size: 125,
        cell: ({ getValue }) => {
            const value = getValue();
            if (value === CLAIM_TOTALS_ROW_KEY) {
                return null;
            }
            return value ? <ClaimStatusIndicator status={value} /> : <SecondaryData />;
        },
    },
];

interface ServicesOverviewProps {
    claimDetails?: ClaimDetails;
    claimDetailsLoading?: boolean;
}

export const ServicesOverview = ({
    claimDetails = undefined,
    claimDetailsLoading = true,
}: ServicesOverviewProps) => (
    <FormSection
        sectionTitle="Services Overview"
        isLoading={claimDetailsLoading}
        content={
            <>
                <DetailLabel label="Claim Totals" />
                <div className="sds_claim-totals">
                    {claimDetails && (claimDetails.services ?? []).length > 0 && (
                        <Grid
                            data={formatGridData(claimDetails)}
                            enableSorting={false}
                            columns={columns}
                        />
                    )}
                    <ClaimCodesContainer
                        className="margin__top--large"
                        categories={[{ label: 'Adjustment Codes', codes: claimDetails?.adjCodes }]}
                    />
                </div>
            </>
        }
    />
);
